import { type Layout, type Rive, useRive } from '@rive-app/react-canvas';
import { useCallback, useState } from 'react';

import { useStateMachineDetection } from '~/utils/RiveUtils';

export type UseRiveWithStateMachinesOptions = {
  src: string;
  autoplay?: boolean;
  layout: Layout;
  onLoadError?: () => void;
  onLoad?: () => void;
};

export type UseRiveWithStateMachinesReturn = {
  RiveComponent: React.ComponentType;
  rive: Rive | null;
  setContainerRef: (ref: HTMLElement | null) => void;
  hasError: boolean;
};

/**
 * Custom hook that handles loading Rive animations with automatic state machine detection.
 * It creates two Rive instances:
 * 1. An inspection instance to detect available state machines
 * 2. An interactive instance with the detected state machines for interactivity
 */
export const useRiveWithStateMachines = ({
  src,
  autoplay = true,
  layout,
  onLoadError,
  onLoad,
}: UseRiveWithStateMachinesOptions): UseRiveWithStateMachinesReturn => {
  const [hasError, setHasError] = useState(false);

  // Sanitize src - convert empty strings to undefined
  const safeSrc = src && src.trim() !== '' ? src : undefined;

  const handleLoadError = useCallback(() => {
    setHasError(true);
    onLoadError?.();
  }, [onLoadError]);

  const handleLoad = useCallback(() => {
    setHasError(false);
    onLoad?.();
  }, [onLoad]);

  // First: Load without state machines to inspect what's available
  const inspectionRive = useRive({
    src: safeSrc,
    autoplay: false,
    layout,
    onLoadError: handleLoadError,
  });

  // Detect state machines from the inspection Rive
  const { detectedStateMachines, detectionComplete } = useStateMachineDetection({
    rive: inspectionRive.rive,
    errorContext: 'Failed to detect Rive state machines',
  });

  // Second: Load with the detected state machines for interactivity
  // Use undefined instead of empty string to prevent Rive initialization error
  const interactiveRive = useRive({
    src: detectionComplete && detectedStateMachines.length > 0 ? safeSrc : undefined,
    autoplay,
    layout,
    stateMachines: detectedStateMachines.length > 0 ? detectedStateMachines : undefined,
    onLoadError: handleLoadError,
    onLoad: handleLoad,
  });

  // Use the appropriate Rive instance
  const activeRive = detectionComplete && detectedStateMachines.length > 0 ? interactiveRive : inspectionRive;

  return {
    ...activeRive,
    hasError,
  };
};
